import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, Bus, ShieldCheck } from 'lucide-react';

const Contact = () => {
    const [formStatus, setFormStatus] = useState('idle');

    const handleSubmit = (e) => {
        e.preventDefault();
        setFormStatus('sending');
        setTimeout(() => {
            setFormStatus('success');
            alert("Thank you! Your message has been received.");
        }, 2000);
    };

    return (
        <div className="font-sans text-gray-800 bg-white pt-28">
            {/* Header */}
            <div className="bg-primary text-white py-32 text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                <div className="container relative z-10">
                    <span className="text-secondary font-bold tracking-widest uppercase mb-4 block">Here to Help</span>
                    <h1 className="text-5xl md:text-7xl font-black mb-6">Contact Us</h1>
                    <p className="max-w-2xl mx-auto text-blue-100 text-xl font-light">
                        Have a query? Reach out to our administrative team. We are happy to assist you with admissions, academics, or general information.
                    </p>
                </div>
            </div>

            {/* Quick Info Grid */}
            <div className="container -mt-16 relative z-20 mb-24">
                <div className="grid md:grid-cols-3 gap-6">
                    <div className="bg-white p-10 rounded-3xl shadow-xl border-t-8 border-blue-500 text-center hover:-translate-y-2 transition-transform">
                        <div className="w-16 h-16 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Phone size={32} />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-2">Speak to Us</h3>
                        <p className="text-gray-500 mb-4">Mon-Sat from 8am to 5pm</p>
                        <a href="tel:+919750978888" className="text-xl font-black text-blue-600 block hover:underline">097509 78888</a>
                        <a href="tel:+919500868888" className="text-lg font-bold text-blue-400 block mt-1 hover:underline">+91 9500868888</a>
                    </div>
                    <div className="bg-white p-10 rounded-3xl shadow-xl border-t-8 border-green-500 text-center hover:-translate-y-2 transition-transform">
                        <div className="w-16 h-16 bg-green-50 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Mail size={32} />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-2">Email Us</h3>
                        <p className="text-gray-500 mb-4">We'll respond within 24 hours</p>
                        <a href="mailto:bharathi@tcmmatricschool.in" className="text-lg font-bold text-green-600 block hover:underline">bharathi@tcmmatricschool.in</a>

                    </div>
                    <div className="bg-white p-10 rounded-3xl shadow-xl border-t-8 border-red-500 text-center hover:-translate-y-2 transition-transform">
                        <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                            <MapPin size={32} />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-2">Visit Campus</h3>
                        <p className="text-gray-500 mb-4">Ammapettai, Erode</p>
                        <p className="text-lg font-bold text-gray-700 leading-snug">
                            Sandaipettai, Sennampatti,<br />
                            Erode, Tamil Nadu 638504
                        </p>
                    </div>
                </div>
            </div>

            {/* Detailed Info & Form Section */}
            <div className="container pb-24">
                <div className="flex flex-col lg:flex-row gap-16">

                    {/* Contact Form */}
                    <div className="lg:w-2/3">
                        <h2 className="text-3xl font-black text-primary mb-2">Send us a Message</h2>
                        <p className="text-gray-500 mb-8">Fill in the details below and our Front Desk Officer will get in touch with you.</p>

                        <form onSubmit={handleSubmit} className="bg-gray-50 p-8 md:p-12 rounded-[2rem] border border-gray-100">
                            <div className="grid md:grid-cols-2 gap-6 mb-6">
                                <div className="space-y-2">
                                    <label className="text-sm font-bold text-gray-700 uppercase tracking-wide">Your Name</label>
                                    <input type="text" className="w-full px-4 py-4 rounded-xl border-gray-200 focus:border-secondary focus:ring-2 focus:ring-secondary/20 outline-none transition-all" placeholder="Enter full name" required />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-bold text-gray-700 uppercase tracking-wide">Phone Number</label>
                                    <input type="tel" className="w-full px-4 py-4 rounded-xl border-gray-200 focus:border-secondary focus:ring-2 focus:ring-secondary/20 outline-none transition-all" placeholder="+91 99999 99999" required />
                                </div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-6 mb-6">
                                <div className="space-y-2">
                                    <label className="text-sm font-bold text-gray-700 uppercase tracking-wide">Email Address</label>
                                    <input type="email" className="w-full px-4 py-4 rounded-xl border-gray-200 focus:border-secondary focus:ring-2 focus:ring-secondary/20 outline-none transition-all" placeholder="you@example.com" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-bold text-gray-700 uppercase tracking-wide">Subject</label>
                                    <select className="w-full px-4 py-4 rounded-xl border-gray-200 focus:border-secondary focus:ring-2 focus:ring-secondary/20 outline-none transition-all bg-white text-gray-600">
                                        <option>General Inquiry</option>
                                        <option>Admissions</option>
                                        <option>Careers / Job Application</option>
                                        <option>Feedback / Complaint</option>
                                    </select>
                                </div>
                            </div>
                            <div className="space-y-2 mb-8">
                                <label className="text-sm font-bold text-gray-700 uppercase tracking-wide">Message</label>
                                <textarea rows="5" className="w-full px-4 py-4 rounded-xl border-gray-200 focus:border-secondary focus:ring-2 focus:ring-secondary/20 outline-none transition-all" placeholder="How can we help you?"></textarea>
                            </div>
                            <button disabled={formStatus === 'sending'} className="w-full bg-primary text-white font-bold text-lg py-5 rounded-xl hover:bg-secondary hover:text-primary transition-all shadow-lg flex items-center justify-center gap-3">
                                {formStatus === 'sending' ? 'Sending...' : 'Send Message'}
                                <Send size={20} />
                            </button>
                        </form>
                    </div>

                    {/* Sidebar Info */}
                    <div className="lg:w-1/3 space-y-8">
                        {/* Transport */}
                        <div className="bg-white p-8 rounded-2xl shadow-lg border-l-4 border-yellow-400">
                            <h3 className="font-bold text-primary text-xl mb-4 flex items-center gap-2"><Bus /> Transport Office</h3>
                            <p className="text-gray-600 text-sm mb-4">For queries regarding bus routes and stops.</p>
                            <div className="font-bold text-gray-800">Mr. Ramesh (Transport Mgr)</div>
                            <a href="tel:+919876543210" className="text-secondary font-bold hover:underline">+91 98765 43210</a>
                        </div>

                        {/* Admin Officer */}
                        <div className="bg-white p-8 rounded-2xl shadow-lg border-l-4 border-purple-400">
                            <h3 className="font-bold text-primary text-xl mb-4 flex items-center gap-2"><ShieldCheck /> Administrative Officer</h3>
                            <p className="text-gray-600 text-sm mb-4">For fee payments and certificates.</p>
                            <div className="font-bold text-gray-800">Mrs. Lakshmi</div>
                            <div className="text-gray-500 text-sm">9:00 AM - 4:00 PM</div>
                        </div>

                        {/* Visiting Hours */}
                        <div className="bg-primary text-white p-8 rounded-2xl shadow-lg">
                            <h3 className="font-bold text-white text-xl mb-4 flex items-center gap-2"><Clock /> Visiting Hours</h3>
                            <ul className="space-y-3 text-sm text-blue-100">
                                <li className="flex justify-between border-b border-white/10 pb-2">
                                    <span>Principal</span>
                                    <span className="font-bold text-white">10:00 AM - 12:00 PM</span>
                                </li>
                                <li className="flex justify-between border-b border-white/10 pb-2">
                                    <span>Office</span>
                                    <span className="font-bold text-white">8:30 AM - 5:30 PM</span>
                                </li>
                                <li className="flex justify-between">
                                    <span>Lunch Break</span>
                                    <span className="font-bold text-white">1:00 PM - 2:00 PM</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Map */}
            <div className="h-[500px] w-full relative">
                <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3908.7758778619623!2d77.81055561480064!3d11.568444391786968!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba965a3c2605ad9%3A0x6331899146f38a0f!2sErode%2C%20Tamil%20Nadu!5e0!3m2!1sen!2sin!4v1645520336589!5m2!1sen!2sin"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    title="Location"
                    className="grayscale hover:grayscale-0 transition-all duration-700"
                ></iframe>
            </div>

        </div>
    );
};
export default Contact;
